<template>
  <div class="activity-page">
    <!-- Page Title -->
    <div class="page-header">
      <h1 class="page-title">{{ $lang.activity_tab }}</h1>
    </div>

    <section v-if="activityCards.length" class="promotion-list">
      <button
        v-for="item in activityCards"
        :key="item.key"
        class="promotion-card"
        :class="'promotion-card--' + item.theme"
        type="button"
        @click="openActivityCard(item)"
      >
        <div class="promotion-card__media">
          <img :src="item.image" :alt="item.title">
        </div>
        <div class="promotion-card__body">
          <p class="promotion-card__label">{{ item.label }}</p>
          <h3 class="promotion-card__title">{{ item.title }}</h3>
          <p v-if="item.desc" class="promotion-card__desc">{{ item.desc }}</p>
        </div>
        <van-icon name="arrow" size="14" color="var(--vk-t3)" />
      </button>
    </section>

    <!-- fail-open 兜底:所有入口都不可见时显示空状态,避免裸空白 -->
    <van-empty
      v-if="nothingVisible"
      :image="require('@/assets/img/common/img_no_data.png')"
      :description="$lang.activity_empty || $lang.noempt || 'No activities available'"
    />

    <login v-model="showLogin" overlay></login>

    <!-- Treasure Box Popup (shared component) -->
    <treasure-box-popup
      v-model="showTreasureBox"
      :price="tbPrice"
      :can-purchase="tbCanPurchase"
      :condition-code="tbConditionCode"
      :total-value="tbTotalValue"
      :rewards="tbRewards"
      :countdown-seconds="tbCountdownSeconds"
    />
  </div>
</template>

<script>
import { TreasureBoxInit, RedPacket, NewPlayerGiftPackInit, GetSignDaily, GetLuckyRoulette, TaskCenterWeeklyInit, GetPromoRule } from '@/api/common'
import TreasureBoxPopup from '@/components/TreasureBoxPopup.vue'
import { failMessage as rewardFailMessage, hasUnavailableMarker, isNeedLogin, isOkResponse, responseContent } from '@/views/details/activity/rewardApi'
import { hasResponseContent, isReadableOkResponse } from '@/views/details/activity/rewardApi'
import { isActivityVisible, normalizeActivityVisibility } from './activityVisibility'

const PROMO_RULE_LIST_KEYS = ['promoRuleList', 'ruleList', 'activityList', 'list', 'dataList', 'records', 'items']
const PROMO_ENVELOPE_KEYS = ['content', 'payload', 'extra', 'page', 'data', 'body']
const REFER_FRIEND_RULE_TOKENS = ['refer', 'friend', 'invite', 'treasure', 'share', '邀请', '好友', '分享', '宝箱']
const MAX_PROMO_ENVELOPE_DEPTH = 8

function safeText(value) {
  return String(value == null ? '' : value).trim()
}

function safeList(value) {
  return Array.isArray(value) ? value : []
}

function collectPromoRules(source, depth = 0, out = [], seen = new Set()) {
  if (!source || typeof source !== 'object' || depth >= MAX_PROMO_ENVELOPE_DEPTH || seen.has(source)) return out
  seen.add(source)
  for (const key of PROMO_RULE_LIST_KEYS) {
    safeList(source[key]).forEach(rule => out.push(rule))
  }
  for (const key of PROMO_ENVELOPE_KEYS) {
    const value = source[key]
    if (value && typeof value === 'object') collectPromoRules(value, depth + 1, out, seen)
  }
  return out
}

function referFriendRuleText(rule) {
  const item = rule && typeof rule === 'object' ? rule : {}
  return [
    item.activityType,
    item.activity_type,
    item.actTypeKey,
    item.act_type_key,
    item.promoType,
    item.promo_type,
    item.promoDetail,
    item.promo_detail,
    item.type,
    item.code,
    item.ruleCode,
    item.rule_code,
    item.name,
    item.title,
    item.descr,
    item.description
  ].map(safeText).join(' ').toLowerCase()
}

function isReferFriendRule(rule) {
  const text = referFriendRuleText(rule)
  return REFER_FRIEND_RULE_TOKENS.some(token => text.includes(token))
}

function isRewardReadUnavailable(res) {
  const root = res && res.response && res.response.data ? res.response.data : res
  const stack = [root, responseContent(root)]
  const seen = new Set()
  while (stack.length) {
    const source = stack.shift()
    if (!source || typeof source !== 'object' || seen.has(source)) continue
    seen.add(source)
    if (hasUnavailableMarker(source)) return true
    const marker = [
      source.fallbackSource,
      source.fallback_source,
      source.source,
      source.reason,
      source.blockedReason,
      source.blocked_reason
    ].filter(Boolean).join(' ').toLowerCase()
    if (marker.includes('read-unavailable') ||
      marker.includes('readunavailable') ||
      marker.includes('missingtable') ||
      marker.includes('missing_table') ||
      marker.includes('missing table') ||
      marker.includes('missingcolumn') ||
      marker.includes('missing_column') ||
      marker.includes('missing column')) return true
    ;['content', 'data', 'body', 'detail', 'diagnostics', 'diagnostic', 'payload', 'extra'].forEach(key => {
      const value = source[key]
      if (value && typeof value === 'object') stack.push(value)
    })
  }
  return false
}

export default {
  name: 'Activity',
  components: { TreasureBoxPopup },
  data() {
    return {
      showLogin: false,
      showTreasureBox: false,
      tbTotalValue: 0,
      tbPrice: 0,
      tbCanPurchase: false,
      tbConditionCode: '',
      tbRewards: [],
      tbCountdownSeconds: 86399,
      tbVisible: false,
      newbieVisible: false,
      redPacketVisible: false,
      redPacketData: null,
      dailyCheckInVisible: false,
      luckyReferralVisible: false,
      weeklyMissionsVisible: false,
      coreActivityReadFailed: false,
      coreActivityReadFailureNotified: false,
      referFriendVisible: false
    }
  },
  mounted() {
    this.initActivityStatus()
  },
  computed: {
    activityCards() {
      const cards = [
        {
          key: 'dailyCheckIn',
          visible: this.dailyCheckInVisible,
          path: '/dailyCheckIn',
          image: require('@/assets/img/activity/activity_banner/activity_checkin.png'),
          theme: 'orange',
          label: this.$lang.activity_active || 'Active',
          title: this.$lang.activity_daily_checkin || 'Daily Check-In',
          desc: this.$lang.activity_daily_checkin_desc || ''
        },
        {
          key: 'referFriend',
          visible: this.referFriendVisible,
          path: '/referFriend',
          image: require('@/assets/img/activity/activity_banner/activity_invite.png'),
          theme: 'orange',
          label: this.$lang.activity_invite_earn || 'Invite Friends',
          title: this.$lang.activity_invite_earn || 'Invite Friends',
          desc: this.$lang.activity_invite_earn_desc || ''
        },
        {
          key: 'treasureBox',
          visible: this.tbVisible,
          action: 'treasureBox',
          image: require('@/assets/img/activity/activity_banner/activity_vip.png'),
          theme: 'gold',
          label: this.$lang.activity_super_value || 'Bonus',
          title: this.$lang.activity_season_pass || 'Mega Value Bonus',
          desc: this.$lang.activity_season_pass_desc || ''
        },
        {
          key: 'newbieGift',
          visible: this.newbieVisible,
          path: '/newbieGift',
          image: require('@/assets/img/activity/activity_icon/activity_gold.png'),
          theme: 'gold',
          label: this.$lang.activity_limited_offers || 'Limited Offers',
          title: this.$lang.activity_welcome_bonus || 'Welcome Bonus',
          desc: this.$lang.activity_welcome_bonus_desc || ''
        },
        {
          key: 'luckyReferral',
          visible: this.luckyReferralVisible,
          path: '/luckyReferral',
          image: require('@/assets/img/activity/activity_icon/activity_1.png'),
          theme: 'orange',
          label: this.$lang.activity_more_events || 'More Events',
          title: this.$lang.activity_lucky_spin || 'Lucky Spin',
          desc: this.$lang.activity_lucky_spin_desc || ''
        },
        {
          key: 'weeklyMissions',
          visible: this.weeklyMissionsVisible,
          path: '/weeklyMissions',
          image: require('@/assets/img/activity/activity_icon/activity_2.png'),
          theme: 'blue',
          label: this.$lang.activity_more_events || 'More Events',
          title: this.$lang.activity_weekly_missions || 'Weekly Missions',
          desc: this.$lang.activity_weekly_missions_desc || ''
        },
        {
          key: 'redPacket',
          visible: this.redPacketVisible,
          path: '/redPacket',
          image: require('@/assets/img/activity/activity_icon/activity_green.png'),
          theme: 'green',
          label: this.$lang.activity_limited_offers || 'Limited Offers',
          title: this.$lang.activity_red_envelope || this.$lang.activity_first_deposit_chest || 'Red Envelope',
          desc: this.$lang.activity_red_envelope_desc || this.$lang.activity_first_deposit_chest_desc || ''
        }
      ]
      return cards.filter(item => item.visible)
    },
    nothingVisible() {
      // 最后防线:fail-open 后基本不会触发;仅当所有入口都不可见(如登录态总后端故障)时显示空状态而非裸空白。
      return this.activityCards.length === 0
    }
  },
  methods: {
    requireLogin() {
      this.showLogin = true
      if (this.$bus) this.$bus.$emit('openLogin')
    },
    isPositiveFlag(value) {
      if (value === true || value === 1) return true
      const normalized = String(value).trim().toLowerCase()
      return ['1', 'true', 'yes', 'open', 'enabled', 'available'].includes(normalized)
    },
    isFalseFlag(value) {
      if (value === false || value === 0) return true
      const normalized = String(value == null ? '' : value).trim().toLowerCase()
      return ['0', 'false', 'no', 'off', 'disabled', 'read_only', 'read-only', 'write_blocked', 'write-blocked', 'technical_marker', 'technicalmarker', 'technical-marker', 'backend_failure', 'backendfailure', 'backend-failure'].includes(normalized)
    },
    isTreasureBoxPurchaseWritable(res, content) {
      const c = content || {}
      return !hasUnavailableMarker(res) &&
        !this.isFalseFlag(c.rewardWritable) &&
        !this.isFalseFlag(c.purchaseWritable) &&
        !this.isFalseFlag(c.writable) &&
        !this.isFalseFlag(c.writeEnabled)
    },
    treasureBoxConditionCode(content) {
      const source = content || {}
      const list = source.boxList || source.list || source.rewards || []
      const first = Array.isArray(list) && list.length > 0 && list[0] ? list[0] : {}
      return String(source.conditionCode || source.currentConditionCode || source.recordNo ||
        first.conditionCode || first.condition_code || '').trim()
    },
    toNumber(value, fallback = 0) {
      const n = Number(value)
      return Number.isFinite(n) ? n : fallback
    },
    normalizeRewards(rewards) {
      return Array.isArray(rewards) ? rewards.filter(Boolean) : []
    },
    showUnavailable(res, fallback) {
      if (!res || isOkResponse(res) || isRewardReadUnavailable(res)) return
      this.$toast({
        message: rewardFailMessage(res, fallback || this.$lang.reward_no_data || 'No rewards found'),
        icon: 'cross'
      })
    },
    showEntryUnavailable(fallback, res) {
      if (!this.$toast) return
      if (isRewardReadUnavailable(res)) return
      this.$toast({
        message: fallback || this.$lang.reward_no_data || 'No rewards found',
        icon: 'cross'
      })
    },
    coreActivityUnavailableMessage() {
      return this.$lang.activity_unavailable ||
        this.$lang.bonus_unavailable_backend ||
        'Activity unavailable'
    },
    closeCoreActivityEntries() {
      this.dailyCheckInVisible = false
      this.luckyReferralVisible = false
      this.weeklyMissionsVisible = false
      this.referFriendVisible = false
    },
    isReadableActivityEntry(res) {
      const c = responseContent(res)
      return isReadableOkResponse(res) && hasResponseContent(c) && isActivityVisible(c, { defaultVisible: true })
    },
    isCoreActivityReadFailure(res) {
      const c = responseContent(res)
      if (!isReadableOkResponse(res) || !hasResponseContent(c)) return true
      return normalizeActivityVisibility(c, { defaultVisible: true }).reason === 'unavailable_marker'
    },
    async initCoreActivityVisibility() {
      const entries = [
        ['dailyCheckInVisible', GetSignDaily],
        ['luckyReferralVisible', GetLuckyRoulette],
        ['weeklyMissionsVisible', TaskCenterWeeklyInit]
      ]
      let readFailed = false
      await Promise.all(entries.map(async ([flag, loader]) => {
        try {
          const result = await loader({})
          const visible = this.isReadableActivityEntry(result)
          this[flag] = visible
          if (this.isCoreActivityReadFailure(result) && !isRewardReadUnavailable(result)) readFailed = true
        } catch (e) {
          this[flag] = false
          if (!isRewardReadUnavailable(e)) readFailed = true
        }
      }))
      // fail-open:单个核心活动接口失败只隐藏该项(上面已 per-item 处理),不再整页弹 "Activity unavailable" 打扰用户。
      this.coreActivityReadFailed = readFailed
    },
    async initReferFriendVisibility() {
      try {
        const res = await GetPromoRule({})
        const content = responseContent(res)
        if (!isReadableOkResponse(res) || !hasResponseContent(content)) {
          this.referFriendVisible = false
          return
        }
        this.referFriendVisible = collectPromoRules(content).some(rule => (
          isReferFriendRule(rule) && isActivityVisible(rule, { defaultVisible: true })
        ))
      } catch (e) {
        this.referFriendVisible = false
      }
    },
    isCoreActivityPathVisible(path) {
      const visibility = {
        '/dailyCheckIn': this.dailyCheckInVisible,
        '/luckyReferral': this.luckyReferralVisible,
        '/weeklyMissions': this.weeklyMissionsVisible,
        '/referFriend': this.referFriendVisible
      }
      if (!Object.prototype.hasOwnProperty.call(visibility, path)) return true
      return !!visibility[path]
    },
    canEnterActivityPath(path) {
      if (!this.isCoreActivityPathVisible(path)) {
        this.showEntryUnavailable(this.coreActivityUnavailableMessage(path))
        return false
      }
      if (path === '/redPacket' && !this.redPacketVisible) {
        this.showEntryUnavailable(this.$lang.rp_round_ended_msg || 'This activity is temporarily unavailable. Please try again later.')
        return false
      }
      if (path === '/newbieGift' && !this.newbieVisible) {
        this.showEntryUnavailable(this.$lang.reward_no_data || 'No rewards found')
        return false
      }
      return true
    },
    async initActivityStatus() {
      if (!this.token) {
        // fail-open:未登录展示静态活动橱窗(导航/展示型入口常驻,点击任意入口再引导登录),
        // 仅与后端数据强相关的入口(宝箱购买态/红包轮次)保持隐藏 —— 不再整页空白。
        this.tbVisible = false
        this.tbCanPurchase = false
        this.redPacketVisible = false
        this.redPacketData = null
        this.newbieVisible = true
        this.dailyCheckInVisible = true
        this.referFriendVisible = true
        this.luckyReferralVisible = true
        this.weeklyMissionsVisible = true
        return
      }
      // Check treasure box visibility
      try {
        const res = await TreasureBoxInit({})
        const c = responseContent(res)
        if (isReadableOkResponse(res) && hasResponseContent(c)) {
          const rewards = this.normalizeRewards(c.rewards)
          this.tbCanPurchase = this.isPositiveFlag(c.canPurchase)
          if (!this.isTreasureBoxPurchaseWritable(res, c)) this.tbCanPurchase = false
          this.tbConditionCode = this.treasureBoxConditionCode(c)
          this.tbPrice = this.toNumber(c.price, this.tbPrice)
          this.tbTotalValue = this.toNumber(c.totalValue, this.tbPrice)
          this.tbRewards = rewards
          this.tbCountdownSeconds = this.toNumber(c.countdownSeconds, this.tbCountdownSeconds)
          this.tbVisible = this.tbCanPurchase && isActivityVisible(c, {
            defaultVisible: !!(this.tbPrice || this.tbTotalValue || rewards.length)
          })
        } else if (isNeedLogin(res)) {
          this.tbVisible = false
          this.tbCanPurchase = false
          this.tbConditionCode = ''
          this.tbRewards = []
        } else {
          this.tbVisible = false
          this.tbCanPurchase = false
          this.tbConditionCode = ''
          this.tbRewards = []
          this.showUnavailable(res, this.$lang.tb_not_available || 'This activity is temporarily unavailable. Please try again later.')
        }
      } catch (e) {
        this.tbVisible = false
        this.tbCanPurchase = false
        this.tbConditionCode = ''
        this.tbRewards = []
        this.showEntryUnavailable(this.$lang.tb_not_available || 'This activity is temporarily unavailable. Please try again later.', e)
      }
      // Check newbie gift visibility
      try {
        const ngRes = await NewPlayerGiftPackInit({})
        const c = responseContent(ngRes)
        if (isReadableOkResponse(ngRes) && hasResponseContent(c)) {
          this.newbieVisible = isActivityVisible(c, { defaultVisible: true })
        } else if (isNeedLogin(ngRes)) {
          this.newbieVisible = false
        } else {
          this.newbieVisible = false
          this.showUnavailable(ngRes, this.$lang.reward_no_data || 'No rewards found')
        }
      } catch (e) {
        this.newbieVisible = false
        this.showEntryUnavailable(this.$lang.reward_no_data || 'No rewards found', e)
      }
      // Check red packet visibility
      if (this.token) {
        try {
          const res = await RedPacket({})
          const c = responseContent(res)
          if (isReadableOkResponse(res) && hasResponseContent(c)) {
            this.redPacketData = c
            this.redPacketVisible = isActivityVisible(c, { defaultVisible: true })
          } else if (isNeedLogin(res)) {
            this.redPacketVisible = false
            this.redPacketData = null
          } else {
            this.redPacketVisible = false
            this.redPacketData = null
            this.showUnavailable(res, this.$lang.rp_round_ended_msg || 'This activity is temporarily unavailable. Please try again later.')
          }
        } catch (e) {
          this.redPacketVisible = false
          this.redPacketData = null
          this.showEntryUnavailable(this.$lang.rp_round_ended_msg || 'This activity is temporarily unavailable. Please try again later.', e)
        }
      }
      await Promise.all([
        this.initCoreActivityVisibility(),
        this.initReferFriendVisibility()
      ])
    },
    goTo(path) {
      if (!this.token) {
        this.requireLogin()
        return
      }
      if (!this.canEnterActivityPath(path)) return
      this.$jumpTo(path)
    },
    openActivityCard(item) {
      if (!item) return
      if (item.action === 'treasureBox') {
        this.openTreasureBox()
        return
      }
      this.goTo(item.path)
    },
    openTreasureBox() {
      if (!this.token) {
        this.requireLogin()
        return
      }
      if (!this.tbVisible || !this.tbCanPurchase) {
        this.showEntryUnavailable(this.$lang.tb_not_available || 'This activity is temporarily unavailable. Please try again later.')
        return
      }
      this.showTreasureBox = true
    }
  }
}
</script>

<style lang="less" scoped>
.activity-page {
  padding: 0 16px 100px;
  min-height: 100vh;
}

.page-header {
  padding: 16px 0 12px;
}

.page-title {
  font-size: var(--vk-fs-xl);
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  letter-spacing: 0;
}

.promotion-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.promotion-card {
  appearance: none;
  width: 100%;
  min-height: 118px;
  border: 1px solid fade(@brand, 18%);
  border-radius: 8px;
  padding: 10px;
  background: linear-gradient(135deg, #33140a 0%, #241c1a 48%, @vk-surface 100%);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: transform 0.15s;

  &:active {
    transform: scale(0.985);
  }

  &:focus-visible {
    border-color: @brand;
  }

  &--gold {
    border-color: fade(@gold, 22%);
    background: linear-gradient(135deg, #3a2506 0%, #312514 48%, @vk-surface 100%);
  }

  &--green {
    border-color: rgba(21, 194, 107, 0.2);
    background: linear-gradient(135deg, #0c2a1b 0%, #1d2a21 48%, @vk-surface 100%);
  }

  &--blue {
    border-color: rgba(88, 145, 255, 0.2);
    background: linear-gradient(135deg, #12233b 0%, #1c2634 48%, @vk-surface 100%);
  }
}

.promotion-card__media {
  width: 132px;
  height: 86px;
  flex: 0 0 132px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.32);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.promotion-card__media img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.promotion-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.promotion-card__label {
  margin: 0;
  font-size: var(--vk-fs-2xs);
  line-height: 1.2;
  font-weight: 700;
  color: @brand;
}

.promotion-card__title {
  margin: 0;
  font-size: var(--vk-fs-base);
  line-height: 1.25;
  font-weight: 800;
  color: #ffffff;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.promotion-card__desc {
  margin: 0;
  font-size: var(--vk-fs-xs);
  line-height: 1.35;
  color: var(--vk-t2);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

@media (max-width: 360px) {
  .activity-page {
    padding-left: 12px;
    padding-right: 12px;
  }

  .promotion-card {
    min-height: 106px;
    gap: 10px;
    padding: 9px;
  }

  .promotion-card__media {
    width: 112px;
    height: 76px;
    flex-basis: 112px;
  }
}
</style>
